import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiXCircle } from 'react-icons/fi';

export interface OptionType {
  key: string | number;
  value: string;
}

interface DropdownWithSearchProps {
  label: string; // Used as a placeholder when no value is selected
  icon?: React.ReactNode;
  options: OptionType[];
  value: string | number | null | undefined;
  onChange: (value: string | number | null) => void;
  className?: string;
  initiator?: (clickCall: () => void, selectedOption?: OptionType) => React.ReactNode;
  maxWidth?: string | number;
  disabled?: boolean;
  name?: string; // Helpful for standard HTML form submissions
}

const DropdownWithSearch: React.FC<DropdownWithSearchProps> = ({
  label,
  icon,
  options,
  value,
  onChange,
  className = '',
  initiator,
  maxWidth,
  disabled = false,
  name
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropUp, setDropUp] = useState(false);

  // Find the currently selected option to display its text
  const selectedOption = options.find((o) => o.key === value);
  const displayLabel = selectedOption ? selectedOption.value : label;

  const filteredOptions = options.filter(o =>
    o.value.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (key: string | number) => {
    onChange(key);
    setOpen(false); // Close dropdown on selection
    setSearch('');  // Reset search when closing
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
        // Optional: clear search when closing by clicking outside
        setSearch(''); 
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    if (disabled) return;
    
    setOpen((prev) => {
      const newState = !prev;

      if (newState && dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Assume dropdown height ~160px
        if (spaceBelow < 160 && spaceAbove > 160) {
          setDropUp(true);
        } else {
          setDropUp(false);
        }
      } else if (!newState) {
         setSearch(''); // Clear search if manually closing
      }

      return newState;
    });
  };

  return (
    <div ref={dropdownRef} className={`position-relative ${className}`}>
      {/* Hidden input for native form submission support */}
      {name && (
        <input type="hidden" name={name} value={value ?? ''} disabled={disabled} />
      )}

      {
        initiator !== undefined ? (
          initiator(() => {
            toggleDropdown();
          }, selectedOption)
        ) : (
          <button
            type="button" // Important so it doesn't accidentally submit forms
            className="btn btn-outline-secondary d-flex align-items-center gap-2 text-truncate"
            onClick={toggleDropdown}
            disabled={disabled}
            style={{ maxWidth: maxWidth ? maxWidth : '100%' }}
          >
            {icon} <span className="text-truncate">{displayLabel}</span>
          </button>
        )
      }

      <AnimatePresence>
        {open && !disabled && (
          <motion.div
            className="position-absolute bg-theme-card border rounded shadow-sm py-2 mt-1"
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            style={{
              zIndex: 1000,
              maxHeight: '250px',
              overflowY: 'auto',
              minWidth: maxWidth !== undefined ? maxWidth : '220px',
              top: dropUp ? 'auto' : '100%',
              bottom: dropUp ? '100%' : 'auto',
              marginTop: dropUp ? undefined : '4px',
              marginBottom: dropUp ? '4px' : undefined,
            }}
          >
            <div className="d-flex justify-content-between align-items-center px-2 mb-2">
              <input
                type="text"
                className="form-control me-2"
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus // Nice UX: focuses search input immediately when opened
              />
              {(search || value !== null && value !== undefined) && (
                <button
                  type="button"
                  className="btn btn-sm btn-link text-danger p-0"
                  title="Clear selection"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearch('');
                    onChange(null);
                  }}
                >
                  <FiXCircle size={18} />
                </button>
              )}
            </div>
            
            <div className="d-flex flex-column">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    title={option.value}
                    onClick={() => handleSelect(option.key)}
                    className={
                      value === option.key // 'value' here is from the component props
                        ? "btn btn-link text-start text-decoration-none dropdown-item active d-flex align-items-center py-2 text-truncate px-3"
                        : "btn btn-link text-start text-decoration-none dropdown-item d-flex align-items-center py-2 text-truncate px-3"
                    }
                    style={{ 
                      maxWidth: maxWidth !== undefined ? maxWidth : "300px",
                      width: '100%'
                    }}
                  >
                    {option.value}
                  </button>
                ))
              ) : (
                <div className="text-muted-theme px-3 py-2 text-center">No matches</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DropdownWithSearch;